import * as React from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2, Mail, Search } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/toast';
import {
  formatEUR,
  useAdminInvoices,
  useAdminInvoiceDownload,
  useResendInvoice,
  type InvoiceDTO,
} from '../../hooks/useInvoices';

export function AdminInvoicesListPage() {
  const invoices = useAdminInvoices();
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    const items = invoices.data?.items ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      return (
        it.invoiceNumber.toLowerCase().includes(q) ||
        it.customer.name.toLowerCase().includes(q) ||
        it.customer.companyName.toLowerCase().includes(q) ||
        it.customer.email.toLowerCase().includes(q)
      );
    });
  }, [invoices.data, search]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>Rechnungen</CardTitle>
              <CardDescription>
                Alle ausgestellten Rechnungen — PDF + X-Rechnung-XML herunterladbar.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Suchen …"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-56"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.isLoading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Noch keine Rechnungen.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nummer</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Zeitraum</TableHead>
                  <TableHead className="text-right">Brutto</TableHead>
                  <TableHead>MwSt-Modus</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((inv) => (
                  <InvoiceRow key={`${inv.uid}-${inv.id}`} invoice={inv} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: InvoiceDTO }) {
  const download = useAdminInvoiceDownload();
  const resend = useResendInvoice();
  const { toast } = useToast();

  async function onDownload(kind: 'pdf' | 'xml') {
    try {
      const url = await download.mutateAsync({
        uid: invoice.uid,
        invoiceId: invoice.id,
        kind,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast({
        title: 'Download fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  async function onResend() {
    if (!confirm(`Rechnung ${invoice.invoiceNumber} erneut versenden?`)) return;
    try {
      await resend.mutateAsync({ uid: invoice.uid, invoiceId: invoice.id });
      toast({ title: 'Rechnung wurde erneut versendet' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <TableRow>
      <TableCell className="font-mono">{invoice.invoiceNumber}</TableCell>
      <TableCell>
        <Link to={`/admin/users/${invoice.uid}`} className="flex flex-col hover:underline">
          <span className="font-medium">
            {invoice.customer.companyName || invoice.customer.name || '—'}
          </span>
          <span className="text-xs text-muted-foreground">{invoice.customer.email}</span>
        </Link>
      </TableCell>
      <TableCell className="text-xs">
        {invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString('de-DE') : '—'}
      </TableCell>
      <TableCell className="text-xs">
        {invoice.periodFrom && invoice.periodTo
          ? `${new Date(invoice.periodFrom).toLocaleDateString('de-DE')} – ${new Date(invoice.periodTo).toLocaleDateString('de-DE')}`
          : '—'}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatEUR(invoice.totals.grossCents)}
      </TableCell>
      <TableCell>
        <Badge variant="outline">{taxLabel(invoice.taxCategory)}</Badge>
      </TableCell>
      <TableCell>
        <Badge
          variant={
            invoice.status === 'sent'
              ? 'success'
              : invoice.status === 'issued'
                ? 'default'
                : invoice.status === 'cancelled'
                  ? 'destructive'
                  : 'muted'
          }
        >
          {invoice.status}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => void onDownload('pdf')}
            disabled={!invoice.pdfStoragePath}
            title="PDF herunterladen"
          >
            <Download className="mr-1 h-3 w-3" /> PDF
          </Button>
          {invoice.xmlStoragePath && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => void onDownload('xml')}
              title="X-Rechnung-XML herunterladen"
            >
              <Download className="mr-1 h-3 w-3" /> XML
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => void onResend()}
            disabled={resend.isPending}
            title="E-Mail erneut versenden"
          >
            <Mail className="mr-1 h-3 w-3" /> Resend
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function taxLabel(c: string): string {
  switch (c) {
    case 'S':
      return 'Standard';
    case 'AE':
      return 'Reverse Charge';
    case 'G':
      return 'Export';
    case 'E':
      return 'Befreit';
    case 'Z':
      return '0%';
    case 'O':
      return 'Out of scope';
    default:
      return c;
  }
}
